import { TableBody } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import useBusy from "@/components/data-table/hooks/useBusy";
import { Table } from "@tanstack/react-table";
import { memo, useCallback, useMemo, useRef } from "react";
import useRowVirtualizer from "@/components/data-table/hooks/useRowVirtualizer";
import SchedulesTableRow from "@/modules/projects/components/schedules/ProjectSchedulesTableRow";
import { useVirtualRowStyle } from "@/components/useVirtualRowStyles";
import { ScheduleWithVisitMap } from "@/modules/projects/components/schedules/types";
import { Virtualizer } from "@tanstack/react-virtual";

interface ProjectScheduleTableBodyProps {
  table: Table<ScheduleWithVisitMap>;
  onRowMouseEnter?: (rowId: string) => void;
  onRowMouseLeave?: (rowId: string) => void;
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>;
  virtualPaddingLeft: number | undefined;
  virtualPaddingRight: number | undefined;
  fixedColumnCount: number;
}

const ProjectSchedulesTableBodyComponent = ({
  table,
  onRowMouseEnter,
  onRowMouseLeave,
  columnVirtualizer,
  virtualPaddingLeft,
  virtualPaddingRight,
  fixedColumnCount,
}: ProjectScheduleTableBodyProps) => {
  const defaultClassName = "grid relative overflow-visible";
  const rows = table.getRowModel().rows;
  const bodyRef = useRef(null);

  const onRowMouseEnterInternal = useCallback(
    (rowId: string) => () => onRowMouseEnter?.(rowId),
    [onRowMouseEnter],
  );

  const onRowMouseLeaveInternal = useCallback(
    (rowId: string) => () => onRowMouseLeave?.(rowId),
    [onRowMouseLeave],
  );

  const isBusy = useBusy();
  const rowVirtualizer = useRowVirtualizer(rows.length, bodyRef);
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  useVirtualRowStyle(rowVirtualizer, bodyRef);

  const style = useMemo(() => {
    return {
      height: `${totalSize}px`, // tells scrollbar how big the table is
    };
  }, [totalSize]);

  return (
    <TableBody
      className={cn(defaultClassName, {
        "opacity-50 pointer-events-none blur-[2px]": isBusy,
      })}
      style={style}
      ref={bodyRef}
    >
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];

        if (row === undefined) {
          return null;
        }
        return (
          <SchedulesTableRow
            key={row.id}
            row={row}
            cells={row.getVisibleCells()}
            onRowMouseEnterInternal={onRowMouseEnterInternal}
            onRowMouseLeaveInternal={onRowMouseLeaveInternal}
            virtualRow={virtualRow}
            columnVirtualizer={columnVirtualizer}
            virtualPaddingLeft={virtualPaddingLeft}
            virtualPaddingRight={virtualPaddingRight}
            fixedColumnCount={fixedColumnCount}
          />
        );
      })}
    </TableBody>
  );
};

export const ProjectSchedulesTableBody = memo(
  ProjectSchedulesTableBodyComponent,
);
